/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  CompanyContainer,
  HeaderTitle,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
  TwoColumns,
  IconButton,
} from '../../../services/settings/appStyle/global.style';
import { Table, IconButton as IconButtonWithText, Loader } from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import { listByManager, listByManagerForReport } from '../../../services/bioplus-services/logBook';
import DocumentService from '../../../services/bioplus-services/document';
import { extractData, getDeclarationQuestions } from './logHelper';
import { ClosedEyeGray, DownloadIcon, EyeIcon } from '../../../Components/icons';
import DeclarationModal from './declarationModalView';
import { IntlMessages } from '../../../helper/langHelper';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import { convertToUserTimeZone } from '../../../helper/dataFormat';

const ManagerLogRecords = () => {
  const userContext = useUserContext();
  const userRole = userContext?.company;
  const premiseId = userRole?.premiseId;

  const [isDeclarationView, setIsDeclarationView] = useState(true);
  const [declarationData, setDeclarationData] = useState();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'logbook');

  const canDownloadReport = !!isUserAllowToManage?.isReport;

  // format language
  const intl = useIntl();
  const alertMessagedeclarationFailed = intl.formatMessage({
    id: 'help.text.declaration.error',
  });

  const query = useQuery(['managerLogBookEntries', page, pageSize], () =>
    listByManager(premiseId, page, pageSize)
  );
  const tableData = query?.data?.data ?? [];

  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  //   table columns
  const userColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.visitor.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName} ${cellData?.row?.original?.userId?.lastName}`}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.company" />,
        accessor: 'companyId.name',
        // width: '22%',
      },
      {
        Header: <IntlMessages id="table.col.premise" />,
        accessor: 'premiseId.propertyName',
      },
      {
        Header: <IntlMessages id="table.col.geofence" />,
        accessor: 'geofenceId.name',
      },
      {
        Header: <IntlMessages id="table.col.service.role" />,
        accessor: 'serviceRole',
      },
      {
        Header: <IntlMessages id="table.col.entry.date" />,
        accessor: 'entryDate',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.entry.time" />,
        accessor: 'entryDate',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? convertToUserTimeZone(cellData.value, userTimeZone) : '--'}
          </TableCell>
        ),
        id: 'entryTime',
      },
      {
        Header: <IntlMessages id="table.col.exit.time" />,
        accessor: 'exitDate',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? convertToUserTimeZone(cellData.value, userTimeZone) : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="text.declaration" />,
        accessor: 'declaration',
        isAction: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDeclarationLink = async (data) => {
    try {
      const content = await DocumentService.getDocument(data?.declarationId);
      const declarationContent = {
        ...content,
        geofenceName: data?.geofenceName,
        premise: data?.premiseName,
      };
      if (content && declarationContent) {
        // const declarationQuestionAnswer = getDeclarationQuestions(declarationContent);
        // if (declarationQuestionAnswer) {
        setDeclarationData(declarationContent);
        setIsDeclarationView(true);
        // }
      }
    } catch (error) {
      alert(alertMessagedeclarationFailed);
    }
  };

  const handleClosed = () => {
    setIsDeclarationView(false);
  };

  const actionList = [
    (data) => (
      <>
        {data?.declarationId ? (
          <IconButton onClick={() => handleDeclarationLink(data)}>
            <EyeIcon style={{ width: '18px', height: '18px' }} />
          </IconButton>
        ) : (
          <IconButton>
            <ClosedEyeGray style={{ width: '18px', height: '18px', color: 'red' }} />
          </IconButton>
        )}
      </>
    ),
  ];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };

  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleExcelExport = async () => {
    try {
      setIsExportLoading(true);

      const reportData = await listByManagerForReport(premiseId);

      const allRecords = reportData?.data ? reportData.data : reportData;

      if (allRecords && Array.isArray(allRecords)) {
        const excelFormatData = extractData(allRecords);
        const fileName = `logRecords_${moment().format('YYYY-MM-DD')}.xlsx`;
        const columnConfig = [
          { header: 'Visitor Name', key: 'VisitorName', width: 30 },
          { header: 'Entity Name', key: 'Company', width: 30 },
          { header: 'premise Name', key: 'premiseName', width: 30 },
          { header: 'Geofence Name', key: 'Geofence', width: 30 },
          { header: 'Service Role', key: 'serviceRole', width: 30 },
          { header: 'Date', key: 'Date', width: 20 },
          { header: 'Entry Time', key: 'entryTime', width: 20 },
          { header: 'Exit Time', key: 'exitTime', width: 20 },
        ];

        exportExcelFile(excelFormatData, fileName, columnConfig);
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export records. Please try again.');
    } finally {
      setIsExportLoading(false);
    }
  };

  if (query.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.records.logRecords" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          {tableData && tableData.length > 0 && canDownloadReport && (
            <IconButtonWithText
              text={<IntlMessages id="button.text.downloadExcel" />}
              icon={<DownloadIcon />}
              onClick={handleExcelExport}
              disabled={isExportLoading}
            />
          )}
        </TwoColumns>
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={tableData}
          columns={userColumns}
          actions={actionList}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={query?.data?.total || 0}
        />
      </TableWrapper>
      {declarationData && (
        <DeclarationModal
          open={isDeclarationView}
          onClose={handleClosed}
          userData={declarationData}
        />
      )}
    </StyledContainer>
  );
};

export default ManagerLogRecords;
